/**
 * accountTypeClassifier — maps a journal account to one of four canonical
 * categories used by the P&L "Account Type" filter:
 *
 *     'sim'     — simulator / paper / playback accounts (no real-money risk)
 *     'eval'    — prop-firm evaluation (challenge) accounts, not yet passed
 *     'funded'  — prop-firm funded accounts (post-evaluation) OR live-funded
 *     'live'    — direct live-broker accounts (real-money brokerage)
 *
 * The user's existing `account.type` field already uses Paper / Evaluation /
 * Funded / Live, so the classifier honors that as the primary signal. Name +
 * id heuristics handle the cases where the type field is missing or doesn't
 * match the account's actual nature (e.g., an account named "Sim101" that
 * was inadvertently created with type=Evaluation).
 *
 * Pure function. No side effects.
 */

export const ACCOUNT_TYPE = Object.freeze({
  SIM:     'sim',
  EVAL:    'eval',
  FUNDED:  'funded',
  LIVE:    'live',
  UNKNOWN: 'unknown',
});

export const ACCOUNT_TYPE_LABEL = Object.freeze({
  [ACCOUNT_TYPE.SIM]:     'Sim',
  [ACCOUNT_TYPE.EVAL]:    'Eval',
  [ACCOUNT_TYPE.FUNDED]:  'Funded',
  [ACCOUNT_TYPE.LIVE]:    'Live',
  [ACCOUNT_TYPE.UNKNOWN]: 'Unknown',
});

// Display order for filter chips — sim/eval/funded/live reads left-to-right
// as the typical trader progression (and matches how Chris phrased it).
export const ACCOUNT_TYPE_ORDER = Object.freeze([
  ACCOUNT_TYPE.SIM,
  ACCOUNT_TYPE.EVAL,
  ACCOUNT_TYPE.FUNDED,
  ACCOUNT_TYPE.LIVE,
]);

/**
 * Classify a single account. Inputs are tolerant of partial / null shapes —
 * a missing account returns 'unknown' rather than throwing.
 *
 * Priority order:
 *   1. Name / id pattern explicitly matches "Sim" or "Playback" → always 'sim'.
 *      This is the override that catches Sim101 with type=Evaluation.
 *   2. Account's own `type` field, if set — Paper → sim, Evaluation → eval,
 *      Funded → funded, Live → live.
 *   3. ID-prefix fallback: BX-M → funded (Bulenox Master), BX → eval,
 *      APEX / TAKEPROFIT → eval.
 *   4. Default → 'unknown'.
 *
 * @param {Object|null} account
 * @returns {string} one of ACCOUNT_TYPE values
 */
export const classifyAccountType = (account) => {
  if (!account || typeof account !== 'object') return ACCOUNT_TYPE.UNKNOWN;

  const id = String(account.id || '').toLowerCase().trim();
  const name = String(account.name || '').toLowerCase().trim();
  const type = String(account.type || '').trim();

  // ── 1. Name / id explicit override for sim accounts ─────────────────────
  // Catches "Sim101", "Sim 101", "Playback101", and similar. The boundary
  // requirement (^/_/space/dash before) avoids matching mid-word "sim" in
  // names like "Premium" or "Simulator-Backed-Eval" where the trader's
  // actual account-status intent might differ.
  const isSimName = (s) => /(^|[\s_-])(sim|playback)/.test(s);
  if (isSimName(id) || isSimName(name)) return ACCOUNT_TYPE.SIM;

  // ── 2. Explicit type field ──────────────────────────────────────────────
  if (type === 'Live')       return ACCOUNT_TYPE.LIVE;
  if (type === 'Funded')     return ACCOUNT_TYPE.FUNDED;
  if (type === 'Paper')      return ACCOUNT_TYPE.SIM;
  if (type === 'Evaluation') return ACCOUNT_TYPE.EVAL;

  // ── 3. ID prefix heuristics (when type is missing/empty) ────────────────
  if (id.startsWith('bx-m') || id.startsWith('bxm')) return ACCOUNT_TYPE.FUNDED;
  if (id.startsWith('bx'))                            return ACCOUNT_TYPE.EVAL;
  if (id.startsWith('apex'))                          return ACCOUNT_TYPE.EVAL;
  if (id.startsWith('takeprofit') || id.startsWith('tpt')) return ACCOUNT_TYPE.EVAL;

  return ACCOUNT_TYPE.UNKNOWN;
};

/**
 * Build a Map<accountId, type> for an accounts list. Cheap to call on every
 * render — pure O(n) — but the call site usually wraps in useMemo against
 * the accounts array.
 *
 * @param {Array<Object>} accounts
 * @returns {Map<string, string>}
 */
export const buildAccountTypeIndex = (accounts) => {
  const m = new Map();
  if (!Array.isArray(accounts)) return m;
  for (const a of accounts) {
    if (a && a.id) m.set(a.id, classifyAccountType(a));
  }
  return m;
};

export default classifyAccountType;
